import { useState, useCallback } from 'react';
import type { Room, RoomInfo, CreateRoomData, RoomContextType } from '@/types/room.types';
import roomAPI from '@/services/room-api';

export function useRoom(): RoomContextType {
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadRooms = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await roomAPI.getUserRooms();
      setRooms(result.data.rooms);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load rooms';
      setError(errorMessage);
      console.error('Error loading rooms:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createRoom = useCallback(async (data: CreateRoomData): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await roomAPI.createRoom(data);
      
      if (result.success && result.data?.room) {
        // Reload rooms to get the updated list
        await loadRooms();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create room';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadRooms]);

  const joinRoom = useCallback(async (roomId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get room details to set as current room
      const roomDetails = await roomAPI.getRoomDetails(roomId);
      if (roomDetails.success && roomDetails.data?.room) {
        setCurrentRoom(roomDetails.data.room);
      }
      // Actual room joining is handled by useRoomConnection hook
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to join room';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteRoom = useCallback(async (roomId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await roomAPI.deleteRoom(roomId);
      
      if (result.success) {
        // Remove the room from local state and reload
        await loadRooms();
        
        // If the deleted room was the current room, clear it
        if (currentRoom?.id === roomId) {
          setCurrentRoom(null);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete room';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadRooms, currentRoom]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const leaveRoom = useCallback(() => {
    // Room leaving is now handled by useRoomConnection hook
    // Just clear React state here
    setCurrentRoom(null);
    setError(null);
  }, []);

  const clearRoomState = useCallback(() => {
    setCurrentRoom(null);
    setError(null);
  }, []);

  return {
    rooms,
    currentRoom,
    isLoading,
    error,
    loadRooms,
    createRoom,
    joinRoom,
    deleteRoom,
    clearError,
    leaveRoom,
    clearRoomState,
    setCurrentRoom
  };
};